import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from '../components/ui/dropdown-menu';
import { Users, Plus, Search, Pencil, Loader2, CalendarIcon, MapPin, UserCheck, UserX, X, Printer, Info, Cake, HelpCircle, LogOut, Home, Building, ChevronUp, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { getPrintToolbarCSS, getPrintToolbarHTML } from '../utils/printHelper';

// Generate room options
const generateRooms = () => {
  const rooms = [];
  // Floor 2 active
  for (let i = 201; i <= 220; i++) {
    rooms.push(i.toString());
  }
  // Floor 1 (inactive but available)
  for (let i = 101; i <= 120; i++) {
    rooms.push(i.toString());
  }
  return rooms;
};

const ROOMS = generateRooms();

export const PatientsPage = () => {
  const { api, canEditPatients, canDischargePatients, isAdmin, isSuperAdmin, isOperator, isKitchen, activeStructure } = useAuth();
  const [patients, setPatients] = useState([]);
  const [diets, setDiets] = useState([]);
  const [allergies, setAllergies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [houseFilter, setHouseFilter] = useState('all'); // Filtro per casa (multi-casa)
  const [floorFilter, setFloorFilter] = useState('all');
  const [presenceFilter, setPresenceFilter] = useState('all'); // all, present, absent
  const [statFilter, setStatFilter] = useState(null); // 'present', 'absent', 'reparto', 'ristorante' - from clicking stats
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPatient, setEditingPatient] = useState(null);
  const [absenceDialogOpen, setAbsenceDialogOpen] = useState(false);
  const [selectedPatientForAbsence, setSelectedPatientForAbsence] = useState(null);
  const [absenceFromDate, setAbsenceFromDate] = useState(null);
  const [absenceUntilDate, setAbsenceUntilDate] = useState(null);
  const [birthdayBannerDismissed, setBirthdayBannerDismissed] = useState(false);
  
  // Discharge confirmation dialog
  const [dischargeDialogOpen, setDischargeDialogOpen] = useState(false);
  const [patientToDischarge, setPatientToDischarge] = useState(null);
  const [dischargeLoading, setDischargeLoading] = useState(false);
  
  // New diet/allergy dialog
  const [dietDialogOpen, setDietDialogOpen] = useState(false);
  const [allergyDialogOpen, setAllergyDialogOpen] = useState(false);
  const [newDietName, setNewDietName] = useState('');
  const [newAllergyName, setNewAllergyName] = useState('');
  
  const structureName = activeStructure?.name || '';
  const isMultiHouse = activeStructure?.is_multi_house || false;
  const houses = activeStructure?.houses || [];
  
  const [formData, setFormData] = useState({
    name: '',
    room: '',
    house_id: '', // Casa per strutture multi-casa
    diets: [],
    allergies: [],
    notes: '',
    meal_location: 'reparto',
    stay_from: null,
    stay_until: null,
    stay_indefinite: true,
    birth_date: null,
    origin: '',
    gender: 'M'
  });

  // Check if user can add diets (only admin)
  const canAddDiets = isAdmin || isSuperAdmin;
  const canAddAllergies = isAdmin || isSuperAdmin || isOperator;

  // Auto-archive patients with expired stay_until
  const autoArchivePatients = useCallback(async () => {
    try {
      const result = await api.post('/patients/auto-archive');
      if (result.data.archived_count > 0) {
        toast.info(`${result.data.archived_count} paziente/i archiviato/i automaticamente (permanenza scaduta)`);
      }
    } catch (error) {
      // Silent fail - not critical
      console.log('Auto-archive check:', error);
    }
  }, [api]);

  const fetchPatients = useCallback(async () => {
    try {
      const params = {};
      if (floorFilter && floorFilter !== 'all') params.floor = floorFilter;
      if (houseFilter && houseFilter !== 'all') params.house_id = houseFilter;
      
      const response = await api.get('/patients', { params });
      setPatients(response.data);
    } catch (error) {
      console.error('Failed to fetch patients:', error);
      toast.error('Errore nel caricamento pazienti');
    } finally {
      setLoading(false);
    }
  }, [api, floorFilter, houseFilter]);

  const fetchDiets = useCallback(async () => {
    try {
      const response = await api.get('/diets');
      setDiets(response.data);
    } catch (error) {
      console.error('Failed to fetch diets:', error);
    }
  }, [api]);

  const fetchAllergies = useCallback(async () => {
    try {
      const response = await api.get('/allergies');
      setAllergies(response.data);
    } catch (error) {
      console.error('Failed to fetch allergies:', error);
    }
  }, [api]);

  // Run auto-archive on mount (once)
  useEffect(() => {
    autoArchivePatients();
  }, [autoArchivePatients]);

  useEffect(() => {
    fetchPatients();
    fetchDiets();
    fetchAllergies();
  }, [fetchPatients, fetchDiets, fetchAllergies]);

  // Add new diet
  const handleAddDiet = async () => {
    if (!newDietName.trim()) return;
    try {
      const code = newDietName.toLowerCase().replace(/\s+/g, '_');
      await api.post('/diets', { 
        name: newDietName.trim(), 
        code
      });
      toast.success('Dieta aggiunta');
      setNewDietName('');
      setDietDialogOpen(false);
      fetchDiets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiunta della dieta');
    }
  };

  // Add new allergy
  const handleAddAllergy = async () => {
    if (!newAllergyName.trim()) return;
    try {
      const code = newAllergyName.toLowerCase().replace(/\s+/g, '_');
      await api.post('/allergies', { 
        name: newAllergyName.trim(), 
        code,
        is_trace: false
      });
      toast.success('Allergia aggiunta');
      setNewAllergyName('');
      setAllergyDialogOpen(false);
      fetchAllergies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiunta dell\'allergia');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.room) {
      toast.error('Nome e camera sono obbligatori');
      return;
    }
    
    // Validazione per strutture multi-casa
    if (isMultiHouse && !formData.house_id) {
      toast.error('Seleziona una casa');
      return;
    }

    try {
      const dataToSend = {
        ...formData,
        stay_from: formData.stay_from ? format(formData.stay_from, 'yyyy-MM-dd') : null,
        stay_until: formData.stay_indefinite ? null : (formData.stay_until ? format(formData.stay_until, 'yyyy-MM-dd') : null),
        birth_date: formData.birth_date ? format(formData.birth_date, 'yyyy-MM-dd') : null,
        house_id: isMultiHouse ? formData.house_id : null
      };
      // Remove the helper field
      delete dataToSend.stay_indefinite;
      
      if (editingPatient) {
        await api.put(`/patients/${editingPatient.id}`, dataToSend);
        toast.success('Paziente aggiornato');
      } else {
        await api.post('/patients', dataToSend);
        toast.success('Paziente creato');
      }
      setDialogOpen(false);
      resetForm();
      fetchPatients();
    } catch (error) {
      console.error('Failed to save patient:', error);
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    }
  };

  // Open discharge confirmation dialog
  const openDischargeDialog = (patient) => {
    setPatientToDischarge(patient);
    setDischargeDialogOpen(true);
  };

  // Confirm discharge
  const confirmDischarge = async () => {
    if (!patientToDischarge) return;
    
    setDischargeLoading(true);
    try {
      await api.post(`/patients/${patientToDischarge.id}/discharge`);
      toast.success('Camera liberata - paziente archiviato');
      setDischargeDialogOpen(false);
      setPatientToDischarge(null);
      fetchPatients();
    } catch (error) {
      console.error('Failed to discharge patient:', error);
      toast.error(error.response?.data?.detail || 'Errore nella liberazione camera');
    } finally {
      setDischargeLoading(false);
    }
  };

  const handleDischarge = (patient) => {
    // Open custom dialog instead of window.confirm
    openDischargeDialog(patient);
  };

  const handleSetAbsence = async () => {
    if (!selectedPatientForAbsence) return;
    
    try {
      const absenceFrom = absenceFromDate ? format(absenceFromDate, 'yyyy-MM-dd') : null;
      const absenceUntil = absenceUntilDate ? format(absenceUntilDate, 'yyyy-MM-dd') : null;
      await api.put(`/patients/${selectedPatientForAbsence.id}`, {
        absence_from: absenceFrom || '',
        absence_until: absenceUntil || ''
      });
      if (absenceFrom && absenceUntil) {
        toast.success(`Paziente assente dal ${format(absenceFromDate, 'd MMM', { locale: it })} al ${format(absenceUntilDate, 'd MMM yyyy', { locale: it })}`);
      } else {
        toast.success('Paziente impostato come presente');
      }
      setAbsenceDialogOpen(false);
      setSelectedPatientForAbsence(null);
      setAbsenceFromDate(null);
      setAbsenceUntilDate(null);
      fetchPatients();
    } catch (error) {
      console.error('Failed to update absence:', error);
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const handleSetPresent = async (patient) => {
    try {
      await api.put(`/patients/${patient.id}`, { absence_from: '', absence_until: '' });
      toast.success(`${patient.name} è ora presente`);
      fetchPatients();
    } catch (error) {
      console.error('Failed to update presence:', error);
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const handleChangeMealLocation = async (patient, location) => {
    try {
      await api.put(`/patients/${patient.id}`, { meal_location: location });
      toast.success(`${patient.name} mangia ${location === 'reparto' ? 'in Reparto' : 'al Ristorante'}`);
      fetchPatients();
    } catch (error) {
      console.error('Failed to update meal location:', error);
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', room: '', house_id: '', diets: [], allergies: [], notes: '', meal_location: 'reparto', stay_from: null, stay_until: null, stay_indefinite: true, birth_date: null, origin: '', gender: 'M' });
    setEditingPatient(null);
  };

  const openEdit = (patient) => {
    setEditingPatient(patient);
    setFormData({
      name: patient.name,
      room: patient.room,
      house_id: patient.house_id || '',
      diets: patient.diets || patient.pathologies || [],
      allergies: patient.allergies || [],
      notes: patient.notes || '',
      meal_location: patient.meal_location || 'reparto',
      stay_from: patient.stay_from ? parseISO(patient.stay_from) : null,
      stay_until: patient.stay_until ? parseISO(patient.stay_until) : null,
      stay_indefinite: !patient.stay_until,
      birth_date: patient.birth_date ? parseISO(patient.birth_date) : null,
      origin: patient.origin || '',
      gender: patient.gender || 'M'
    });
    setDialogOpen(true);
  };

  const openAbsenceDialog = (patient) => {
    setSelectedPatientForAbsence(patient);
    setAbsenceFromDate(patient.absence_from ? parseISO(patient.absence_from) : null);
    setAbsenceUntilDate(patient.absence_until ? parseISO(patient.absence_until) : null);
    setAbsenceDialogOpen(true);
  };

  const toggleDiet = (dietCode) => {
    setFormData(prev => ({
      ...prev,
      diets: prev.diets.includes(dietCode)
        ? prev.diets.filter(c => c !== dietCode)
        : [...prev.diets, dietCode]
    }));
  };

  const toggleAllergy = (allergyCode) => {
    setFormData(prev => ({
      ...prev,
      allergies: prev.allergies.includes(allergyCode)
        ? prev.allergies.filter(c => c !== allergyCode)
        : [...prev.allergies, allergyCode]
    }));
  };

  // Filter and sort patients
  const filteredPatients = patients.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.room.includes(searchTerm);
    
    const matchesPresence = presenceFilter === 'all' ||
      (presenceFilter === 'present' && p.is_present) ||
      (presenceFilter === 'absent' && !p.is_present);
    
    // Stat filter from clicking on stats cards
    const matchesStatFilter = !statFilter ||
      (statFilter === 'present' && p.is_present) ||
      (statFilter === 'absent' && !p.is_present) ||
      (statFilter === 'reparto' && p.meal_location === 'reparto') ||
      (statFilter === 'ristorante' && p.meal_location === 'ristorante');
    
    return matchesSearch && matchesPresence && matchesStatFilter;
  }).sort((a, b) => {
    // Sort by room number (which inherently sorts by floor since room 101 < 201)
    return a.room.localeCompare(b.room, undefined, { numeric: true });
  });

  // Toggle stat filter - click same filter again to clear
  const toggleStatFilter = (filter) => {
    setStatFilter(prev => prev === filter ? null : filter);
  };

  const getDietName = (code) => {
    const diet = diets.find(c => c.code === code);
    return diet?.name || code;
  };

  const getAllergyName = (code) => {
    const allergy = allergies.find(a => a.code === code);
    return allergy?.name || code;
  };

  const getPatientCardClass = (patient) => {
    let classes = 'patient-card';
    if (!patient.is_present) classes += ' opacity-60';
    
    const patientDiets = patient.diets || patient.pathologies || [];
    const hasDisfagia = patientDiets.some(c => c.includes('disfagia'));
    const hasDiabete = patientDiets.includes('diabete');
    
    if (hasDisfagia && hasDiabete) return classes + ' has-multiple';
    if (hasDisfagia) return classes + ' has-disfagia';
    if (hasDiabete) return classes + ' has-diabete';
    return classes;
  };

  // Print occupancy report
  const handlePrintOccupancy = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Popup bloccato. Abilita i popup per stampare.');
      return;
    }

    // Group patients by floor and room
    const allPatients = patients.sort((a, b) => a.room.localeCompare(b.room));
    const floors = [...new Set(allPatients.map(p => p.room.charAt(0)))].sort();
    
    const today = new Date().toLocaleDateString('it-IT', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Occupazione Struttura - ${today}</title>
        <style>
          ${getPrintToolbarCSS()}
          @page { 
            size: A4 portrait; 
            margin: 15mm;
          }
          * { 
            box-sizing: border-box; 
            margin: 0; 
            padding: 0;
          }
          body { 
            font-family: Arial, sans-serif; 
            font-size: 10pt;
            color: #333;
          }
          .header {
            text-align: center;
            margin-bottom: 20px;
            padding-bottom: 10px;
            border-bottom: 2px solid #0284c7;
          }
          .structure-name {
            font-size: 14pt;
            font-weight: bold;
            color: #0284c7;
            margin-bottom: 5px;
          }
          .header h1 {
            font-size: 18pt;
            color: #0284c7;
            margin-bottom: 5px;
          }
          .header p {
            font-size: 11pt;
            color: #666;
          }
          .stats {
            display: flex;
            justify-content: center;
            gap: 30px;
            margin-bottom: 20px;
            padding: 10px;
            background: #f8fafc;
            border-radius: 8px;
          }
          .stat-item {
            text-align: center;
          }
          .stat-value {
            font-size: 24pt;
            font-weight: bold;
            color: #0284c7;
          }
          .stat-label {
            font-size: 9pt;
            color: #666;
          }
          .floor-section {
            margin-bottom: 20px;
          }
          .floor-title {
            font-size: 12pt;
            font-weight: bold;
            color: #0284c7;
            margin-bottom: 10px;
            padding: 5px 10px;
            background: #e0f2fe;
            border-radius: 4px;
          }
          .patients-table {
            width: 100%;
            border-collapse: collapse;
          }
          .patients-table th {
            background: #f1f5f9;
            padding: 8px;
            text-align: left;
            font-size: 9pt;
            border-bottom: 2px solid #e2e8f0;
          }
          .patients-table td {
            padding: 6px 8px;
            border-bottom: 1px solid #e2e8f0;
            font-size: 9pt;
          }
          .patients-table tr:nth-child(even) {
            background: #f8fafc;
          }
          .room-cell {
            font-weight: bold;
            width: 60px;
          }
          .name-cell {
            width: 200px;
          }
          .conditions-cell {
            font-size: 8pt;
          }
          .condition-badge {
            display: inline-block;
            padding: 2px 6px;
            border-radius: 4px;
            margin: 1px;
            font-size: 7pt;
            font-weight: 500;
          }
          .location-cell {
            width: 80px;
          }
          .status-cell {
            width: 70px;
          }
          .absent {
            color: #dc2626;
            font-style: italic;
          }
          .present {
            color: #16a34a;
          }
          .legend {
            margin-top: 20px;
            padding: 10px;
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 4px;
          }
          .legend h3 {
            font-size: 10pt;
            color: #0284c7;
            margin-bottom: 8px;
          }
          .legend-grid {
            display: flex;
            flex-wrap: wrap;
            gap: 10px;
          }
          .legend-item {
            display: flex;
            align-items: center;
            gap: 5px;
            font-size: 8pt;
          }
          .legend-color {
            width: 12px;
            height: 12px;
            border-radius: 3px;
          }
          .footer {
            margin-top: 15px;
            text-align: center;
            font-size: 8pt;
            color: #999;
          }
          @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>
        ${getPrintToolbarHTML('Occupazione Struttura')}
        <div class="print-content">
        <div class="header">
          <div class="structure-name">${structureName}</div>
          <h1>Occupazione Struttura</h1>
          <p>${today}</p>
        </div>
        
        <div class="stats">
          <div class="stat-item">
            <div class="stat-value">${patients.length}</div>
            <div class="stat-label">Totale Pazienti</div>
          </div>
          <div class="stat-item">
            <div class="stat-value" style="color: #16a34a">${patients.filter(p => p.is_present).length}</div>
            <div class="stat-label">Presenti</div>
          </div>
          <div class="stat-item">
            <div class="stat-value" style="color: #dc2626">${patients.filter(p => !p.is_present).length}</div>
            <div class="stat-label">Assenti</div>
          </div>
          <div class="stat-item">
            <div class="stat-value" style="color: #0284c7">${patients.filter(p => p.is_present && p.meal_location === 'reparto').length}</div>
            <div class="stat-label">In Reparto</div>
          </div>
          <div class="stat-item">
            <div class="stat-value" style="color: #8b5cf6">${patients.filter(p => p.is_present && p.meal_location === 'ristorante').length}</div>
            <div class="stat-label">In Ristorante</div>
          </div>
        </div>

        ${floors.map(floor => {
          const floorPatients = allPatients.filter(p => p.room.charAt(0) === floor);
          return `
            <div class="floor-section">
              <div class="floor-title">Piano ${floor} (${floorPatients.length} pazienti)</div>
              <table class="patients-table">
                <thead>
                  <tr>
                    <th>Camera</th>
                    <th>Nome</th>
                    <th>Condizioni</th>
                    <th>Luogo Pasto</th>
                    <th>Stato</th>
                  </tr>
                </thead>
                <tbody>
                  ${floorPatients.map(patient => `
                    <tr>
                      <td class="room-cell">${patient.room}</td>
                      <td class="name-cell">${patient.name}</td>
                      <td class="conditions-cell">
                        ${(patient.diets || patient.pathologies || []).map(code => {
                          const diet = diets.find(c => c.code === code);
                          const color = diet?.color || '#64748b';
                          const name = diet?.name || code;
                          return `<span class="condition-badge" style="background: ${color}20; color: ${color}; border: 1px solid ${color}">${name}</span>`;
                        }).join('')}
                        ${(patient.allergies || []).map(code => {
                          const allergy = allergies.find(a => a.code === code);
                          const name = allergy?.name || code;
                          return `<span class="condition-badge" style="background: #fff7ed; color: #ea580c; border: 1px solid #fed7aa">⚠️ ${name}</span>`;
                        }).join('')}
                      </td>
                      <td class="location-cell">${patient.meal_location === 'reparto' ? 'Reparto' : 'Ristorante'}</td>
                      <td class="status-cell ${patient.is_present ? 'present' : 'absent'}">
                        ${patient.is_present ? 'Presente' : 'Assente'}
                      </td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
          `;
        }).join('')}

        <div class="legend">
          <h3>Legenda</h3>
          <div class="legend-grid">
            <div class="legend-item"><strong>Profilo Nutrizionale:</strong></div>
            ${diets.map(diet => `
              <div class="legend-item">
                <div class="legend-color" style="background: ${diet.color || '#64748b'}"></div>
                <span>${diet.name}</span>
              </div>
            `).join('')}
            <div class="legend-item"><strong>Allergie:</strong></div>
            ${allergies.map(allergy => `
              <div class="legend-item">
                <div class="legend-color" style="background: #ea580c"></div>
                <span>⚠️ ${allergy.name}</span>
              </div>
            `).join('')}
          </div>
        </div>
        
        <div class="footer">
          Stampato il ${new Date().toLocaleDateString('it-IT')} alle ${new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
        </div>
        </div>
      </body>
      </html>
    `;
    
    printWindow.document.write(html);
    printWindow.document.close();
  };

  // Print single floor occupancy
  const handlePrintFloor = (floor) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Popup bloccato. Abilita i popup per stampare.');
      return;
    }

    const floorPatients = patients.filter(p => p.room.charAt(0) === floor).sort((a, b) => a.room.localeCompare(b.room));
    const today = new Date().toLocaleDateString('it-IT', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Occupazione Piano ${floor} - ${today}</title>
        <style>
          ${getPrintToolbarCSS()}
          @page { size: A4 portrait; margin: 15mm; }
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { font-family: Arial, sans-serif; font-size: 10pt; color: #333; }
          .header { text-align: center; margin-bottom: 20px; padding-bottom: 10px; border-bottom: 2px solid #0284c7; }
          .structure-name { font-size: 14pt; font-weight: bold; color: #0284c7; margin-bottom: 5px; }
          .header h1 { font-size: 18pt; color: #0284c7; margin-bottom: 5px; }
          .header p { font-size: 11pt; color: #666; }
          .stats { display: flex; justify-content: center; gap: 30px; margin-bottom: 20px; padding: 10px; background: #f8fafc; border-radius: 8px; }
          .stat-item { text-align: center; }
          .stat-value { font-size: 24pt; font-weight: bold; color: #0284c7; }
          .stat-label { font-size: 9pt; color: #666; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background: #0284c7; color: white; font-size: 9pt; }
          td { font-size: 9pt; }
          .room { font-weight: bold; text-align: center; width: 60px; }
          .location-reparto { background: #dbeafe; }
          .location-ristorante { background: #fef3c7; }
          .absent { color: #dc2626; font-style: italic; }
          .footer { text-align: center; margin-top: 20px; font-size: 8pt; color: #999; }
          @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
        </style>
      </head>
      <body>
        ${getPrintToolbarHTML('Piano ' + floor)}
        <div class="print-content">
        <div class="header">
          <div class="structure-name">${activeStructure?.name || 'Struttura'}</div>
          <h1>OCCUPAZIONE PIANO ${floor}</h1>
          <p>${today}</p>
        </div>
        <div class="stats">
          <div class="stat-item"><div class="stat-value">${floorPatients.length}</div><div class="stat-label">Pazienti</div></div>
          <div class="stat-item"><div class="stat-value">${floorPatients.filter(p => p.is_present).length}</div><div class="stat-label">Presenti</div></div>
          <div class="stat-item"><div class="stat-value">${floorPatients.filter(p => !p.is_present).length}</div><div class="stat-label">Assenti</div></div>
        </div>
        <table>
          <thead><tr><th>Camera</th><th>Nome</th><th>Diete</th><th>Allergie</th><th>Pasto</th><th>Note</th></tr></thead>
          <tbody>
            ${floorPatients.map(p => `
              <tr class="${!p.is_present ? 'absent' : ''} ${p.meal_location === 'ristorante' ? 'location-ristorante' : 'location-reparto'}">
                <td class="room">${p.room}</td>
                <td>${p.name}${!p.is_present ? ' (Assente)' : ''}</td>
                <td>${(p.diets || p.pathologies || []).map(code => diets.find(d => d.code === code)?.name || code).join(', ') || '-'}</td>
                <td>${(p.allergies || []).map(code => allergies.find(a => a.code === code)?.name || code).join(', ') || '-'}</td>
                <td>${p.meal_location === 'ristorante' ? 'Ristorante' : 'Reparto'}</td>
                <td style="max-width:150px;overflow:hidden;text-overflow:ellipsis;">${p.notes || '-'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        <div class="footer">Stampato il ${new Date().toLocaleDateString('it-IT')} alle ${new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</div>
        </div>
      </body>
      </html>
    `;
    
    printWindow.document.write(html);
    printWindow.document.close();
  };

  // Print single patient card (A4)
  const handlePrintPatientCard = (patient) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Popup bloccato. Abilita i popup per stampare.');
      return;
    }

    const today = new Date().toLocaleDateString('it-IT', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    const patientDiets = (patient.diets || patient.pathologies || []).map(code => diets.find(d => d.code === code)?.name || code);
    const patientAllergies = (patient.allergies || []).map(code => allergies.find(a => a.code === code)?.name || code);

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Scheda Paziente - ${patient.name}</title>
        <style>
          ${getPrintToolbarCSS()}
          @page { size: A4 portrait; margin: 20mm; }
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { font-family: Arial, sans-serif; font-size: 11pt; color: #333; }
          .header { text-align: center; margin-bottom: 30px; padding-bottom: 15px; border-bottom: 3px solid #0284c7; }
          .structure-name { font-size: 12pt; font-weight: bold; color: #0284c7; margin-bottom: 5px; }
          .header h1 { font-size: 24pt; color: #333; margin-bottom: 5px; }
          .header .room { font-size: 18pt; color: #0284c7; font-weight: bold; }
          .header p { font-size: 10pt; color: #666; margin-top: 10px; }
          .section { margin-bottom: 25px; }
          .section-title { font-size: 14pt; font-weight: bold; color: #0284c7; border-bottom: 2px solid #0284c7; padding-bottom: 5px; margin-bottom: 15px; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; }
          .info-item { padding: 10px; background: #f8fafc; border-radius: 8px; }
          .info-label { font-size: 9pt; color: #666; text-transform: uppercase; margin-bottom: 5px; }
          .info-value { font-size: 12pt; font-weight: 500; }
          .tags { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 10px; }
          .tag { display: inline-block; padding: 5px 12px; border-radius: 20px; font-size: 10pt; }
          .tag-diet { background: #dbeafe; color: #1e40af; }
          .tag-allergy { background: #fee2e2; color: #991b1b; }
          .notes-box { background: #fef3c7; padding: 15px; border-radius: 8px; border-left: 4px solid #f59e0b; }
          .footer { text-align: center; margin-top: 40px; font-size: 9pt; color: #999; border-top: 1px solid #ddd; padding-top: 15px; }
          @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
        </style>
      </head>
      <body>
        ${getPrintToolbarHTML('Scheda ' + patient.name)}
        <div class="print-content">
        <div class="header">
          <div class="structure-name">${activeStructure?.name || 'Struttura'}</div>
          <h1>${patient.name}</h1>
          <div class="room">Camera ${patient.room}</div>
          <p>${patient.patient_code ? 'Codice: ' + patient.patient_code : ''}</p>
        </div>

        <div class="section">
          <div class="section-title">Informazioni Generali</div>
          <div class="info-grid">
            <div class="info-item"><div class="info-label">Data di Nascita</div><div class="info-value">${patient.birth_date ? format(parseISO(patient.birth_date), 'd MMMM yyyy', { locale: it }) : 'Non specificata'}</div></div>
            <div class="info-item"><div class="info-label">Sesso</div><div class="info-value">${patient.gender === 'M' ? 'Maschio' : patient.gender === 'F' ? 'Femmina' : 'Altro'}</div></div>
            <div class="info-item"><div class="info-label">Provenienza</div><div class="info-value">${patient.origin || 'Non specificata'}</div></div>
            <div class="info-item"><div class="info-label">Luogo Pasto</div><div class="info-value">${patient.meal_location === 'ristorante' ? 'Ristorante' : 'Reparto'}</div></div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">Permanenza</div>
          <div class="info-grid">
            <div class="info-item"><div class="info-label">Dal</div><div class="info-value">${patient.stay_from ? format(parseISO(patient.stay_from), 'd MMMM yyyy', { locale: it }) : 'Non specificata'}</div></div>
            <div class="info-item"><div class="info-label">Al</div><div class="info-value">${patient.stay_until ? format(parseISO(patient.stay_until), 'd MMMM yyyy', { locale: it }) : 'Indeterminato'}</div></div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">Diete</div>
          <div class="tags">
            ${patientDiets.length > 0 ? patientDiets.map(d => `<span class="tag tag-diet">${d}</span>`).join('') : '<span style="color:#999">Nessuna dieta registrata</span>'}
          </div>
        </div>

        <div class="section">
          <div class="section-title">Allergie Alimentari</div>
          <div class="tags">
            ${patientAllergies.length > 0 ? patientAllergies.map(a => `<span class="tag tag-allergy">⚠ ${a}</span>`).join('') : '<span style="color:#999">Nessuna allergia registrata</span>'}
          </div>
        </div>

        ${patient.notes ? `
          <div class="section">
            <div class="section-title">Note</div>
            <div class="notes-box">${patient.notes}</div>
          </div>
        ` : ''}

        <div class="footer">
          Scheda generata il ${new Date().toLocaleDateString('it-IT')} alle ${new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
        </div>
        </div>
      </body>
      </html>
    `;
    
    printWindow.document.write(html);
    printWindow.document.close();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-700" />
      </div>
    );
  }

  // Helper: Calculate days until stay_until
  const getDaysRemaining = (patient) => {
    if (!patient.stay_until) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const stayUntil = parseISO(patient.stay_until);
    const diffTime = stayUntil.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Stats
  const presentCount = patients.filter(p => p.is_present).length;
  const absentCount = patients.filter(p => !p.is_present).length;
  const inRepartoCount = patients.filter(p => p.is_present && p.meal_location === 'reparto').length;
  const inRistoranteCount = patients.filter(p => p.is_present && p.meal_location === 'ristorante').length;
  
  // Available floors (derived from patients)
  const availableFloors = [...new Set(patients.map(p => p.room.charAt(0)))].sort();
  
  // Birthday check
  const today = new Date();
  const birthdayPatients = patients.filter(p => {
    if (!p.birth_date) return false;
    const birthDate = parseISO(p.birth_date);
    return today.getDate() === birthDate.getDate() && today.getMonth() === birthDate.getMonth();
  });

  return (
    <div className="space-y-6 animate-fade-in" data-testid="patients-page">
      {/* Birthday Banner */}
      {birthdayPatients.length > 0 && !birthdayBannerDismissed && (
        <div className="bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-lg p-4 shadow-lg relative">
          <button 
            onClick={() => setBirthdayBannerDismissed(true)}
            className="absolute top-2 right-2 text-white/70 hover:text-white transition-colors"
            aria-label="Chiudi"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3 pr-8">
            <Cake className="w-8 h-8" />
            <div>
              <p className="font-bold text-lg">
                🎂 {birthdayPatients.length === 1 ? 'Compleanno oggi!' : `${birthdayPatients.length} compleanni oggi!`}
              </p>
              <p className="text-pink-100">
                {birthdayPatients.map(p => {
                  const age = today.getFullYear() - parseISO(p.birth_date).getFullYear();
                  return `${p.name} (${age} anni)`;
                }).join(', ')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Users className="w-7 h-7 text-blue-700" />
            Pazienti
          </h1>
          <p className="text-slate-500 mt-1">Gestisci l'anagrafica pazienti</p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Print Options Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="outline" 
                size="sm"
                data-testid="print-options-btn"
                className="text-xs sm:text-sm"
              >
                <Printer className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Stampa</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Opzioni di Stampa</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handlePrintOccupancy} data-testid="print-structure-btn">
                <Building className="w-4 h-4 mr-2" />
                Occupazione Struttura
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs text-slate-500">Per Piano</DropdownMenuLabel>
              {availableFloors.map(floor => (
                <DropdownMenuItem key={floor} onClick={() => handlePrintFloor(floor)}>
                  <Home className="w-4 h-4 mr-2" />
                  Occupazione Piano {floor}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {canEditPatients && (
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button className="bg-blue-700 hover:bg-blue-800 text-xs sm:text-sm" size="sm" data-testid="add-patient-btn">
                  <Plus className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Nuovo Paziente</span>
                  <span className="sm:hidden">Nuovo</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    {editingPatient ? 'Modifica Paziente' : 'Nuovo Paziente'}
                  </DialogTitle>
                </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nome Completo *</Label>
                <Input
                  id="name"
                  data-testid="patient-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Mario Rossi"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="room">Camera *</Label>
                <Select
                  value={formData.room}
                  onValueChange={(value) => setFormData({ ...formData, room: value })}
                >
                  <SelectTrigger data-testid="patient-room-select">
                    <SelectValue placeholder="Seleziona camera" />
                  </SelectTrigger>
                  <SelectContent>
                    {ROOMS.map(room => (
                      <SelectItem key={room} value={room}>
                        Camera {room}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Casa - solo per strutture multi-casa */}
              {isMultiHouse && houses.length > 0 && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-purple-600" />
                    Casa *
                  </Label>
                  <Select
                    value={formData.house_id}
                    onValueChange={(value) => setFormData({ ...formData, house_id: value })}
                  >
                    <SelectTrigger data-testid="patient-house-select">
                      <SelectValue placeholder="Seleziona casa" />
                    </SelectTrigger>
                    <SelectContent>
                      {houses.map(house => (
                        <SelectItem key={house.id} value={house.id}>
                          <span className="flex items-center gap-2">
                            <span className="font-medium">{house.prefix}</span> - {house.name}
                            {!house.requires_meal_service && <span className="text-xs text-slate-400">(App.)</span>}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label>Luogo Pasto</Label>
                <Select
                  value={formData.meal_location}
                  onValueChange={(value) => setFormData({ ...formData, meal_location: value })}
                >
                  <SelectTrigger data-testid="patient-meal-location">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="reparto">In Reparto</SelectItem>
                    <SelectItem value="ristorante">Al Ristorante (Sala)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Gender */}
              <div className="space-y-2">
                <Label>Sesso</Label>
                <Select
                  value={formData.gender}
                  onValueChange={(value) => setFormData({ ...formData, gender: value })}
                >
                  <SelectTrigger data-testid="patient-gender">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="M">Maschio</SelectItem>
                    <SelectItem value="F">Femmina</SelectItem>
                    <SelectItem value="X">Altro</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Birth Date - Simple inputs */}
              <div className="space-y-2">
                <Label>Data di Nascita</Label>
                <div className="flex gap-2">
                  <Select
                    value={formData.birth_date ? String(formData.birth_date.getDate()) : ''}
                    onValueChange={(day) => {
                      const current = formData.birth_date || new Date(1980, 0, 1);
                      const newDate = new Date(current.getFullYear(), current.getMonth(), parseInt(day));
                      setFormData({ ...formData, birth_date: newDate });
                    }}
                  >
                    <SelectTrigger className="w-20" data-testid="birth-day">
                      <SelectValue placeholder="GG" />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                        <SelectItem key={d} value={String(d)}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={formData.birth_date ? String(formData.birth_date.getMonth()) : ''}
                    onValueChange={(month) => {
                      const current = formData.birth_date || new Date(1980, 0, 1);
                      const newDate = new Date(current.getFullYear(), parseInt(month), current.getDate());
                      setFormData({ ...formData, birth_date: newDate });
                    }}
                  >
                    <SelectTrigger className="w-28" data-testid="birth-month">
                      <SelectValue placeholder="Mese" />
                    </SelectTrigger>
                    <SelectContent>
                      {['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 
                        'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'].map((m, i) => (
                        <SelectItem key={i} value={String(i)}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={formData.birth_date ? String(formData.birth_date.getFullYear()) : ''}
                    onValueChange={(year) => {
                      const current = formData.birth_date || new Date(1980, 0, 1);
                      const newDate = new Date(parseInt(year), current.getMonth(), current.getDate());
                      setFormData({ ...formData, birth_date: newDate });
                    }}
                  >
                    <SelectTrigger className="w-24" data-testid="birth-year">
                      <SelectValue placeholder="Anno" />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 106 }, (_, i) => new Date().getFullYear() - i).map(y => (
                        <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formData.birth_date && (
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="icon"
                      onClick={() => setFormData({ ...formData, birth_date: null })}
                      className="text-slate-400 hover:text-red-500"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>

              {/* Origin/Provenienza */}
              <div className="space-y-2">
                <Label htmlFor="origin">Provenienza</Label>
                <Input
                  id="origin"
                  data-testid="patient-origin-input"
                  value={formData.origin}
                  onChange={(e) => setFormData({ ...formData, origin: e.target.value })}
                  placeholder="Es: Ospedale San Raffaele, Casa di cura X..."
                />
              </div>

              {/* Diets Section */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Profilo Nutrizionale</Label>
                  {canAddDiets && (
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setDietDialogOpen(true)}
                      className="text-blue-700 h-6 px-2"
                    >
                      <Plus className="w-3 h-3 mr-1" /> Aggiungi
                    </Button>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {diets.map(diet => (
                    <Badge
                      key={diet.id}
                      variant={formData.diets.includes(diet.code) ? "default" : "outline"}
                      className="cursor-pointer transition-colors"
                      style={{
                        backgroundColor: formData.diets.includes(diet.code) ? (diet.color || '#3b82f6') : 'transparent',
                        color: formData.diets.includes(diet.code) ? 'white' : (diet.color || '#3b82f6'),
                        borderColor: diet.color || '#3b82f6'
                      }}
                      onClick={() => toggleDiet(diet.code)}
                      data-testid={`diet-badge-${diet.code}`}
                    >
                      {diet.name}
                    </Badge>
                  ))}
                  {diets.length === 0 && (
                    <p className="text-sm text-slate-400">Nessuna dieta definita</p>
                  )}
                </div>
              </div>

              {/* Allergies Section */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Allergie Alimentari</Label>
                  {canAddAllergies && (
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setAllergyDialogOpen(true)}
                      className="text-orange-600 h-6 px-2"
                    >
                      <Plus className="w-3 h-3 mr-1" /> Aggiungi
                    </Button>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {allergies.map(allergy => (
                    <Badge
                      key={allergy.id}
                      variant={formData.allergies.includes(allergy.code) ? "default" : "outline"}
                      className="cursor-pointer transition-colors"
                      style={{
                        backgroundColor: formData.allergies.includes(allergy.code) ? '#f97316' : 'transparent',
                        color: formData.allergies.includes(allergy.code) ? 'white' : '#f97316',
                        borderColor: '#f97316'
                      }}
                      onClick={() => toggleAllergy(allergy.code)}
                      data-testid={`allergy-badge-${allergy.code}`}
                    >
                      {allergy.name}
                    </Badge>
                  ))}
                  {allergies.length === 0 && (
                    <p className="text-sm text-slate-400">Nessuna allergia definita</p>
                  )}
                </div>
              </div>

              {/* Permanenza in struttura */}
              <div className="space-y-3 border rounded-lg p-4 bg-slate-50">
                <Label className="text-sm font-medium">Permanenza in Struttura</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs text-slate-500">Dal:</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left" data-testid="stay-from-btn">
                          <CalendarIcon className="w-4 h-4 mr-2" />
                          {formData.stay_from ? format(formData.stay_from, 'd MMM yyyy', { locale: it }) : 'Data ingresso'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={formData.stay_from}
                          onSelect={(date) => setFormData({ ...formData, stay_from: date })}
                          locale={it}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-xs text-slate-500">Al:</Label>
                    {formData.stay_indefinite ? (
                      <Button 
                        variant="outline" 
                        className="w-full justify-start text-left text-slate-400"
                        onClick={() => setFormData({ ...formData, stay_indefinite: false })}
                        data-testid="stay-until-indefinite"
                      >
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        Indeterminato
                      </Button>
                    ) : (
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-start text-left" data-testid="stay-until-btn">
                            <CalendarIcon className="w-4 h-4 mr-2" />
                            {formData.stay_until ? format(formData.stay_until, 'd MMM yyyy', { locale: it }) : 'Data uscita'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={formData.stay_until}
                            onSelect={(date) => setFormData({ ...formData, stay_until: date })}
                            locale={it}
                            disabled={(date) => formData.stay_from && date < formData.stay_from}
                          />
                        </PopoverContent>
                      </Popover>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="stay_indefinite"
                    checked={formData.stay_indefinite}
                    onChange={(e) => setFormData({ ...formData, stay_indefinite: e.target.checked, stay_until: e.target.checked ? null : formData.stay_until })}
                    className="rounded border-slate-300"
                  />
                  <Label htmlFor="stay_indefinite" className="text-sm text-slate-600 cursor-pointer">
                    Permanenza indeterminata
                  </Label>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Note</Label>
                <Textarea
                  id="notes"
                  data-testid="patient-notes-input"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Note aggiuntive..."
                  rows={3}
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Annulla
                </Button>
                <Button type="submit" className="bg-blue-700 hover:bg-blue-800" data-testid="save-patient-btn">
                  {editingPatient ? 'Aggiorna' : 'Crea'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
          )}

          {/* Add Diet Dialog */}
          <Dialog open={dietDialogOpen} onOpenChange={setDietDialogOpen}>
            <DialogContent className="sm:max-w-sm">
              <DialogHeader>
                <DialogTitle>Nuova Dieta</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Nome Dieta</Label>
                  <Input
                    value={newDietName}
                    onChange={(e) => setNewDietName(e.target.value)}
                    placeholder="Es: Dieta Iperproteica"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setDietDialogOpen(false)}>Annulla</Button>
                  <Button onClick={handleAddDiet} className="bg-blue-700 hover:bg-blue-800">Aggiungi</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Add Allergy Dialog */}
          <Dialog open={allergyDialogOpen} onOpenChange={setAllergyDialogOpen}>
            <DialogContent className="sm:max-w-sm">
              <DialogHeader>
                <DialogTitle>Nuova Allergia</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Nome Allergia</Label>
                  <Input
                    value={newAllergyName}
                    onChange={(e) => setNewAllergyName(e.target.value)}
                    placeholder="Es: Frutta a guscio"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setAllergyDialogOpen(false)}>Annulla</Button>
                  <Button onClick={handleAddAllergy} className="bg-orange-600 hover:bg-orange-700">Aggiungi</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats - Clickable filters */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card 
          className={`border-slate-200 cursor-pointer transition-all hover:shadow-md ${statFilter === 'present' ? 'ring-2 ring-emerald-500 bg-emerald-50' : ''}`}
          onClick={() => toggleStatFilter('present')}
          data-testid="stat-presenti"
        >
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <UserCheck className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{presentCount}</p>
                <p className="text-xs text-slate-500">Presenti</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card 
          className={`border-slate-200 cursor-pointer transition-all hover:shadow-md ${statFilter === 'absent' ? 'ring-2 ring-red-500 bg-red-50' : ''}`}
          onClick={() => toggleStatFilter('absent')}
          data-testid="stat-assenti"
        >
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <UserX className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{absentCount}</p>
                <p className="text-xs text-slate-500">Assenti</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card 
          className={`border-slate-200 cursor-pointer transition-all hover:shadow-md ${statFilter === 'reparto' ? 'ring-2 ring-blue-500 bg-blue-50' : ''}`}
          onClick={() => toggleStatFilter('reparto')}
          data-testid="stat-reparto"
        >
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{inRepartoCount}</p>
                <p className="text-xs text-slate-500">In Reparto</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card 
          className={`border-slate-200 cursor-pointer transition-all hover:shadow-md ${statFilter === 'ristorante' ? 'ring-2 ring-amber-500 bg-amber-50' : ''}`}
          onClick={() => toggleStatFilter('ristorante')}
          data-testid="stat-ristorante"
        >
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{inRistoranteCount}</p>
                <p className="text-xs text-slate-500">Al Ristorante</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Active filter indicator */}
      {statFilter && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-slate-500">Filtro attivo:</span>
          <Badge 
            variant="outline" 
            className={`cursor-pointer ${
              statFilter === 'present' ? 'bg-emerald-100 text-emerald-700 border-emerald-300' :
              statFilter === 'absent' ? 'bg-red-100 text-red-700 border-red-300' :
              statFilter === 'reparto' ? 'bg-blue-100 text-blue-700 border-blue-300' :
              'bg-amber-100 text-amber-700 border-amber-300'
            }`}
            onClick={() => setStatFilter(null)}
          >
            {statFilter === 'present' ? 'Presenti' :
             statFilter === 'absent' ? 'Assenti' :
             statFilter === 'reparto' ? 'In Reparto' : 'Al Ristorante'}
            <X className="w-3 h-3 ml-1" />
          </Badge>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Cerca per nome o camera..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-patients"
          />
        </div>
        
        {/* Filtro Casa - solo per strutture multi-casa */}
        {isMultiHouse && houses.length > 0 && (
          <Select value={houseFilter} onValueChange={(value) => { setHouseFilter(value); setFloorFilter('all'); }}>
            <SelectTrigger className={`w-48 gap-2 ${houseFilter !== 'all' ? 'border-purple-300 bg-purple-50' : ''}`} data-testid="house-filter">
              <Building2 className={`w-4 h-4 ${houseFilter !== 'all' ? 'text-purple-600' : 'text-slate-500'} flex-shrink-0`} />
              <SelectValue placeholder="Casa" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                <span className="flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Tutte le case
                </span>
              </SelectItem>
              {houses.map(house => (
                <SelectItem key={house.id} value={house.id}>
                  <span className="flex items-center gap-2">
                    <Home className={`w-4 h-4 ${house.requires_meal_service ? 'text-green-500' : 'text-slate-400'}`} />
                    <span className="font-medium">{house.prefix}</span> - {house.name}
                    {!house.requires_meal_service && <span className="text-xs text-slate-400">(App.)</span>}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        
        <Select value={floorFilter} onValueChange={setFloorFilter}>
          <SelectTrigger className="w-44 gap-2" data-testid="floor-filter">
            <Building className="w-4 h-4 text-slate-500 flex-shrink-0" />
            <SelectValue placeholder="Piano" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              <span className="flex items-center gap-2">
                <Building className="w-4 h-4" />
                Tutti i piani
              </span>
            </SelectItem>
            {/* Mostra piani dinamici dalla casa selezionata o dalla struttura */}
            {(isMultiHouse && houseFilter !== 'all'
              ? houses.find(h => h.id === houseFilter)?.floors || []
              : activeStructure?.floors || ['1', '2']
            ).map(floor => (
              <SelectItem key={floor} value={floor}>
                <span className="flex items-center gap-2">
                  <ChevronUp className="w-4 h-4 text-blue-500" />
                  Piano {floor}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={presenceFilter} onValueChange={setPresenceFilter}>
          <SelectTrigger className={`w-44 gap-2 ${presenceFilter === 'absent' ? 'border-red-300 bg-red-50' : presenceFilter === 'present' ? 'border-green-300 bg-green-50' : ''}`} data-testid="presence-filter">
            {presenceFilter === 'present' ? (
              <UserCheck className="w-4 h-4 text-green-600 flex-shrink-0" />
            ) : presenceFilter === 'absent' ? (
              <UserX className="w-4 h-4 text-red-600 flex-shrink-0" />
            ) : (
              <Users className="w-4 h-4 text-slate-500 flex-shrink-0" />
            )}
            <SelectValue placeholder="Presenza" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              <span className="flex items-center gap-2">
                <Users className="w-4 h-4 text-slate-500" />
                Tutti i pazienti
              </span>
            </SelectItem>
            <SelectItem value="present">
              <span className="flex items-center gap-2">
                <UserCheck className="w-4 h-4 text-green-600" />
                <span className="text-green-700">Solo presenti</span>
              </span>
            </SelectItem>
            <SelectItem value="absent">
              <span className="flex items-center gap-2">
                <UserX className="w-4 h-4 text-red-600" />
                <span className="text-red-700">Solo assenti</span>
              </span>
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Patients List - 1 per row with all info */}
      <div className="space-y-3">
        {filteredPatients.map((patient) => (
          <div 
            key={patient.id} 
            className={`p-3 sm:p-4 rounded-lg border bg-white ${!patient.is_present ? 'border-red-200 bg-red-50/30' : 'border-slate-200'} hover:shadow-sm transition-shadow`}
            data-testid={`patient-card-${patient.id}`}
          >
            {/* Header row: Room + Name + Actions */}
            <div className="flex items-start gap-3">
              {/* Room badge - con casa per multi-house */}
              <div className="flex flex-col items-center gap-1">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-base sm:text-lg font-bold text-blue-700">{patient.room}</span>
                </div>
                {/* Casa badge per strutture multi-casa */}
                {isMultiHouse && patient.house_id && (() => {
                  const house = houses.find(h => h.id === patient.house_id);
                  return house ? (
                    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${house.requires_meal_service ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-500'}`}>
                      {house.prefix}
                    </span>
                  ) : null;
                })()}
              </div>
              
              {/* Main content */}
              <div className="min-w-0 flex-1">
                {/* Name row */}
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <span className="font-semibold text-slate-900 text-base sm:text-lg truncate">{patient.name}</span>
                    
                    {/* Birthday Badge - Show if today is birthday */}
                    {patient.birth_date && (() => {
                      const today = new Date();
                      const birthDate = parseISO(patient.birth_date);
                      const isBirthday = today.getDate() === birthDate.getDate() && 
                                         today.getMonth() === birthDate.getMonth();
                      return isBirthday ? (
                        <Badge className="bg-pink-500 text-white animate-pulse text-[10px] px-1.5 py-0.5">
                          <Cake className="w-3 h-3" />
                        </Badge>
                      ) : null;
                    })()}
                  </div>
                  
                  {/* Actions - Compact on mobile */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {!isKitchen && patient.is_present && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8 sm:h-auto sm:w-auto sm:px-2 border-slate-200 text-slate-600 hover:bg-slate-50"
                            data-testid={`presence-menu-${patient.id}`}
                          >
                            <HelpCircle className="w-4 h-4" />
                            <span className="hidden sm:inline ml-1 text-xs">Dove?</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                          <DropdownMenuLabel className="text-xs text-slate-500">
                            Il paziente è in struttura?
                          </DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => openAbsenceDialog(patient)}
                            className="text-red-600 cursor-pointer"
                          >
                            <LogOut className="w-4 h-4 mr-2" />
                            <div>
                              <p className="font-medium">È uscito temporaneamente</p>
                              <p className="text-xs text-slate-500">Segna assenza con date</p>
                            </div>
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleChangeMealLocation(patient, patient.meal_location === 'reparto' ? 'ristorante' : 'reparto')}
                            className="cursor-pointer"
                          >
                            <MapPin className="w-4 h-4 mr-2" />
                            <div>
                              <p className="font-medium">Cambia luogo pasto</p>
                              <p className="text-xs text-slate-500">
                                Ora: {patient.meal_location === 'reparto' ? 'Reparto → Ristorante' : 'Ristorante → Reparto'}
                              </p>
                            </div>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                    {!isKitchen && !patient.is_present && (
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handleSetPresent(patient)}
                        className="h-8 w-8 border-emerald-200 text-emerald-600 hover:bg-emerald-50"
                        data-testid={`set-present-${patient.id}`}
                        title="È tornato"
                      >
                        <Home className="w-4 h-4" />
                      </Button>
                    )}
                    {canEditPatients && (
                      <>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEdit(patient)}
                          className="h-8 w-8"
                          data-testid={`edit-patient-${patient.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handlePrintPatientCard(patient)}
                          className="h-8 w-8 text-slate-600 hover:text-blue-600 hover:bg-blue-50"
                          title="Stampa scheda paziente"
                          data-testid={`print-patient-${patient.id}`}
                        >
                          <Printer className="w-4 h-4" />
                        </Button>
                        {canDischargePatients && (() => {
                          const daysRemaining = getDaysRemaining(patient);
                          const isUrgent = daysRemaining !== null && daysRemaining <= 3;
                          
                          return (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDischarge(patient)}
                              className={`h-8 w-8 ${
                                isUrgent 
                                  ? 'text-red-600 hover:text-red-700 hover:bg-red-50 animate-pulse' 
                                  : 'text-orange-600 hover:text-orange-700 hover:bg-orange-50'
                              }`}
                              data-testid={`discharge-patient-${patient.id}`}
                              title="Libera camera"
                            >
                              <UserX className="w-4 h-4" />
                            </Button>
                          );
                        })()}
                      </>
                    )}
                  </div>
                </div>
                
                {/* Info badges row */}
                <div className="flex items-center flex-wrap gap-1.5 mt-1">
                  {/* Patient Code */}
                  {patient.patient_code && (
                    <span className="text-[10px] font-mono text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">
                      {patient.patient_code}
                    </span>
                  )}
                  
                  {/* Meal Location Badge */}
                  <Badge 
                    variant="outline" 
                    className={`text-[10px] px-1.5 py-0 ${patient.meal_location === 'ristorante' ? 'bg-amber-50 text-amber-600 border-amber-200' : 'bg-blue-50 text-blue-600 border-blue-200'}`}
                  >
                    <MapPin className="w-2.5 h-2.5 mr-0.5" />
                    {patient.meal_location === 'ristorante' ? 'Rist.' : 'Rep.'}
                  </Badge>
                  
                  {/* Absence Badge */}
                  {!patient.is_present && (
                    <Badge variant="outline" className="bg-red-50 text-red-600 border-red-200 text-[10px] px-1.5 py-0">
                      Assente
                    </Badge>
                  )}
                </div>
                
                {/* Details - More compact on mobile */}
                <div className="text-xs text-slate-500 mt-1 space-y-0.5">
                  {/* Birth Date */}
                  {patient.birth_date && (
                    <p>
                      <span className="font-medium">Nato/a:</span>{' '}
                      {format(parseISO(patient.birth_date), 'd MMM yyyy', { locale: it })}
                      {(() => {
                        const today = new Date();
                        const birthDate = parseISO(patient.birth_date);
                        const age = today.getFullYear() - birthDate.getFullYear();
                        return ` (${age} anni)`;
                      })()}
                    </p>
                  )}
                  
                  {/* Stay period */}
                  {patient.stay_from && (
                    <p>
                      <span className="font-medium">Permanenza:</span>{' '}
                      {format(parseISO(patient.stay_from), 'd MMM yyyy', { locale: it })}
                      {patient.stay_until 
                        ? ` → ${format(parseISO(patient.stay_until), 'd MMM yyyy', { locale: it })}`
                        : ' → Indeterminata'
                      }
                    </p>
                  )}
                  
                  {/* Absence period */}
                  {(patient.absence_from || patient.absence_until) && !patient.is_present && (
                    <p className="text-red-500">
                      <span className="font-medium">Assenza:</span>{' '}
                      {patient.absence_from && patient.absence_until 
                        ? `${format(parseISO(patient.absence_from), 'd MMM', { locale: it })} → ${format(parseISO(patient.absence_until), 'd MMM', { locale: it })}`
                        : patient.absence_until 
                          ? `Fino al ${format(parseISO(patient.absence_until), 'd MMM', { locale: it })}`
                          : ''
                      }
                    </p>
                  )}
                </div>
              </div>
            </div>
            
            {/* Details row: Diets, Allergies, Notes */}
            <div className="mt-3 pt-3 border-t border-slate-100">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {/* Diets */}
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-1">Profilo Nutrizionale</p>
                  {(patient.diets || patient.pathologies)?.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {(patient.diets || patient.pathologies).map(code => (
                        <span
                          key={code}
                          className="text-xs px-2 py-1 rounded-full border bg-blue-50 text-blue-700 border-blue-200"
                        >
                          {getDietName(code)}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-xs text-slate-400">Nessuna</span>
                  )}
                </div>
                
                {/* Allergies */}
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-1">Allergie</p>
                  {patient.allergies?.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {patient.allergies.map(code => (
                        <span
                          key={code}
                          className="text-xs px-2 py-1 rounded-full border bg-orange-50 text-orange-600 border-orange-200"
                        >
                          ⚠️ {getAllergyName(code)}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-xs text-slate-400">Nessuna</span>
                  )}
                </div>
                
                {/* Notes */}
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-1">Note</p>
                  {patient.notes ? (
                    <p className="text-xs text-slate-600 bg-slate-50 p-2 rounded">{patient.notes}</p>
                  ) : (
                    <span className="text-xs text-slate-400">Nessuna nota</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredPatients.length === 0 && (
        <div className="text-center py-12">
          <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">Nessun paziente trovato</p>
        </div>
      )}

      {/* Discharge Confirmation Dialog */}
      <Dialog open={dischargeDialogOpen} onOpenChange={setDischargeDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <UserX className="w-5 h-5" />
              Libera Camera
            </DialogTitle>
          </DialogHeader>
          {patientToDischarge && (
            <div className="space-y-4">
              <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <p className="text-slate-700">
                  Stai per liberare la camera <strong className="text-orange-600">{patientToDischarge.room}</strong> di:
                </p>
                <p className="text-xl font-bold text-slate-900 mt-2">{patientToDischarge.name}</p>
                {patientToDischarge.patient_code && (
                  <p className="text-sm text-slate-500 mt-1">Codice: {patientToDischarge.patient_code}</p>
                )}
              </div>
              
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-700">
                  <strong>Nota:</strong> Il paziente verrà archiviato e resterà visibile nello storico. 
                  Non sarà più nella lista pazienti attivi.
                </p>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setDischargeDialogOpen(false);
                    setPatientToDischarge(null);
                  }}
                  disabled={dischargeLoading}
                >
                  Annulla
                </Button>
                <Button 
                  onClick={confirmDischarge}
                  disabled={dischargeLoading}
                  className="bg-orange-600 hover:bg-orange-700 text-white"
                  data-testid="confirm-discharge-btn"
                >
                  {dischargeLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Liberando...
                    </>
                  ) : (
                    <>
                      <UserX className="w-4 h-4 mr-2" />
                      Conferma Liberazione
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Absence Dialog */}
      <Dialog open={absenceDialogOpen} onOpenChange={setAbsenceDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Imposta Assenza</DialogTitle>
          </DialogHeader>
          {selectedPatientForAbsence && (
            <div className="space-y-4">
              <p className="text-slate-600">
                Imposta il periodo di assenza per <strong>{selectedPatientForAbsence.name}</strong>
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Da:</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left">
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        {absenceFromDate ? format(absenceFromDate, 'd MMM', { locale: it }) : 'Data inizio'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={absenceFromDate}
                        onSelect={setAbsenceFromDate}
                        locale={it}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                
                <div className="space-y-2">
                  <Label>A:</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left">
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        {absenceUntilDate ? format(absenceUntilDate, 'd MMM', { locale: it }) : 'Data fine'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={absenceUntilDate}
                        onSelect={setAbsenceUntilDate}
                        locale={it}
                        disabled={(date) => absenceFromDate && date < absenceFromDate}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {(absenceFromDate || absenceUntilDate) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setAbsenceFromDate(null); setAbsenceUntilDate(null); }}
                  className="text-slate-500"
                >
                  <X className="w-4 h-4 mr-1" />
                  Rimuovi date (rendi presente)
                </Button>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setAbsenceDialogOpen(false)}>
                  Annulla
                </Button>
                <Button 
                  onClick={handleSetAbsence}
                  className={(absenceFromDate && absenceUntilDate) ? "bg-red-600 hover:bg-red-700" : "bg-emerald-600 hover:bg-emerald-700"}
                >
                  {(absenceFromDate && absenceUntilDate) ? 'Imposta Assenza' : 'Rendi Presente'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
